import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Chip,
  Tooltip
} from '@mui/material';
import { 
  PhotoCamera, 
  Save, 
  ContentCopy, 
  Launch,
  Palette,
  Link as LinkIcon
} from '@mui/icons-material';
import AuthService from '../services/AuthService';
import WhiteLabelService from '../services/WhiteLabelService';
import LoadingIndicator from '../components/LoadingIndicator';
import toastService from '../utils/toastService';
import { PrimaryButton } from '../components';
import { TipoUsuario } from '../types/enums/TipoUsuario';
import { ConfiguracaoWhiteLabel } from '../types/models';
import PlanUsageCard from '../components/PlanUsageCard';

const Profile: React.FC = () => {
  const { user, updateUserInfo } = useAuth();
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estados para White Label
  const [whiteLabelConfig, setWhiteLabelConfig] = useState<ConfiguracaoWhiteLabel>({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [linkPersonalizado, setLinkPersonalizado] = useState('');
  const [isWhiteLabelEnabled, setIsWhiteLabelEnabled] = useState(false);
  const [linkDisponivel, setLinkDisponivel] = useState<boolean | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      
      // Carregar configurações White Label se for consultor
      if (user.tipoUsuario === TipoUsuario.CONSULTOR) {
        carregarConfiguracaoWhiteLabel();
      }
    }
  }, [user]);

  const carregarConfiguracaoWhiteLabel = async () => {
    try {
      const configuracao = await WhiteLabelService.obterConfiguracao();
      setWhiteLabelConfig(configuracao);
      setIsWhiteLabelEnabled(configuracao.ativoWhiteLabel || false);
      setLinkPersonalizado(configuracao.linkPersonalizado || '');
      if (configuracao.logoPersonalizadaUrl) {
        setPreviewUrl(configuracao.logoPersonalizadaUrl);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração White Label:', error);
    }
  };

  const handleNameUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await AuthService.updateUserName(name);

      if (user) {
        updateUserInfo({
          ...user,
          name: name
        });
      }

      toastService.success('Nome atualizado com sucesso!');
    } catch (err: any) {
      toastService.error(err.response?.data?.message || 'Falha ao atualizar o nome. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toastService.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      await AuthService.updateUserPassword(
        currentPassword,
        newPassword
      );

      toastService.success('Senha atualizada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toastService.error(err.response?.data?.message || 'Falha ao atualizar a senha. Verifique se a senha atual está correta.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhiteLabelToggle = async (enabled: boolean) => {
    setIsWhiteLabelEnabled(enabled);
    const novaConfiguracao = {
      ...whiteLabelConfig,
      ativoWhiteLabel: enabled
    };
    setWhiteLabelConfig(novaConfiguracao);
    
    try {
      await WhiteLabelService.atualizarConfiguracao(novaConfiguracao);
      toastService.success('Configuração White Label atualizada!');
    } catch (error) {
      console.error('Erro ao atualizar configuração White Label:', error);
      toastService.error('Erro ao atualizar configuração White Label');
      setIsWhiteLabelEnabled(!enabled);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewUrl(result);
        const novaConfiguracao = {
          ...whiteLabelConfig,
          logoPersonalizadaUrl: result
        };
        setWhiteLabelConfig(novaConfiguracao);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorChange = (tipo: 'primaria' | 'secundaria', cor: string) => {
    const novaConfiguracao = {
      ...whiteLabelConfig,
      [tipo === 'primaria' ? 'corPrimaria' : 'corSecundaria']: cor
    };
    setWhiteLabelConfig(novaConfiguracao);
  };

  const gerarLinkAleatorio = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `consultor-${timestamp}-${random}`;
  };

  const handleGerarLinkAutomatico = () => {
    const novoLink = gerarLinkAleatorio();
    setLinkPersonalizado(novoLink);
  };

  const validarLinkPersonalizado = async (link: string) => {
    if (!link) {
      setLinkDisponivel(null);
      return;
    }
    
    try {
      const disponivel = await WhiteLabelService.validarLinkPersonalizado(link);
      setLinkDisponivel(disponivel);
    } catch (error) {
      console.error('Erro ao validar link:', error);
      setLinkDisponivel(false);
    }
  };

  const handleLinkChange = (novoLink: string) => {
    const linkLimpo = novoLink.toLowerCase().replaceAll(/[^a-z0-9-]/g, '');
    setLinkPersonalizado(linkLimpo);
    
    // Validar após um delay para evitar muitas chamadas
    if (linkLimpo.length >= 3) {
      setTimeout(() => validarLinkPersonalizado(linkLimpo), 500);
    } else {
      setLinkDisponivel(null);
    }
  };

  const handleSaveWhiteLabel = async () => {
    setLoading(true);
    try {
      let linkFinal = linkPersonalizado;
      
      // Se não há link personalizado, gerar um automaticamente
      if (!linkFinal && isWhiteLabelEnabled) {
        linkFinal = gerarLinkAleatorio();
        setLinkPersonalizado(linkFinal);
      }
      
      const configuracaoFinal = {
        ...whiteLabelConfig,
        linkPersonalizado: linkFinal || undefined
      };
      await WhiteLabelService.atualizarConfiguracao(configuracaoFinal);
      toastService.success('Configuração White Label salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração White Label:', error);
      toastService.error('Erro ao salvar configuração White Label');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (linkPersonalizado) {
      const url = `${globalThis.location.origin}/c/${linkPersonalizado}`;
      navigator.clipboard.writeText(url).then(() => {
        toastService.success('Link copiado para a área de transferência!');
      });
    }
  };

  const handleOpenLink = () => {
    if (linkPersonalizado) {
      const url = `${globalThis.location.origin}/c/${linkPersonalizado}`;
      globalThis.open(url, '_blank');
    }
  };

  if (loading) {
    return <LoadingIndicator message="Atualizando informações..." />;
  }

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Meu Perfil
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleNameUpdate} sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Informações Pessoais
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                value={user?.email}
                margin="normal"
                disabled
                helperText="O email não pode ser alterado"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PrimaryButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!name || name === user?.name}
              >
                Atualizar Nome
              </PrimaryButton>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Plan Usage Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Informações do Plano
          </Typography>
          <PlanUsageCard />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Configurações White Label para Consultores */}
        {user?.tipoUsuario === TipoUsuario.CONSULTOR && (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                <Palette sx={{ mr: 1, verticalAlign: 'middle' }} />
                Personalização White Label
              </Typography>
              
              <Box sx={{ mb: 2, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  <strong>White Label:</strong> Personalize a aparência da plataforma com sua marca. 
                  Seus clientes verão seu logo e cores ao acessarem através do seu link personalizado.
                </Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={isWhiteLabelEnabled}
                    onChange={(e) => handleWhiteLabelToggle(e.target.checked)}
                  />
                }
                label="Ativar Personalização White Label"
                sx={{ mb: 2 }}
              />

              {isWhiteLabelEnabled && (
                <Card sx={{ p: 3, mb: 3 }}>
                  <CardContent>
                    {/* Upload de Logo */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Logo Personalizada
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={previewUrl}
                          sx={{ width: 80, height: 80 }}
                        >
                          {!previewUrl && 'Logo'}
                        </Avatar>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="logo-upload"
                          type="file"
                          onChange={handleImageUpload}
                        />
                        <label htmlFor="logo-upload">
                          <IconButton color="primary" component="span">
                            <PhotoCamera />
                          </IconButton>
                        </label>
                      </Box>
                    </Box>

                    {/* Cores do Tema */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Cores do Tema
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            type="color"
                            label="Cor Primária"
                            value={whiteLabelConfig.corPrimaria || '#EF443C'}
                            onChange={(e) => handleColorChange('primaria', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            type="color"
                            label="Cor Secundária"
                            value={whiteLabelConfig.corSecundaria || '#042E4B'}
                            onChange={(e) => handleColorChange('secundaria', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Link Personalizado */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Link Personalizado
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                          fullWidth
                          label="Seu Link Personalizado"
                          value={linkPersonalizado}
                          onChange={(e) => handleLinkChange(e.target.value)}
                          helperText={`Seus clientes acessarão: thinkway.app/c/${linkPersonalizado || 'seulink'}`}
                          error={linkDisponivel === false}
                          color={linkDisponivel === true ? 'success' : undefined}
                        />
                        <PrimaryButton
                          variant="outlined"
                          onClick={handleGerarLinkAutomatico}
                          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                        >
                          Gerar Automaticamente
                        </PrimaryButton>
                      </Box>
                      
                      {/* Status da validação do link */}
                      {linkPersonalizado && linkDisponivel !== null && (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={linkDisponivel ? 'Link disponível' : 'Link já está em uso'}
                            color={linkDisponivel ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                      )}
                      
                      {linkPersonalizado && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            URL gerada:
                          </Typography>
                          <Chip 
                            label={`thinkway.app/c/${linkPersonalizado}`}
                            variant="outlined"
                            size="small"
                            sx={{ flexGrow: 1 }}
                          />
                          <Tooltip title="Copiar Link">
                            <IconButton onClick={handleCopyLink} size="small" color="primary">
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Abrir em Nova Aba">
                            <IconButton onClick={handleOpenLink} size="small" color="primary">
                              <Launch />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    {/* Preview */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Preview
                      </Typography>
                      <Box 
                        sx={{ 
                          p: 2, 
                          backgroundColor: whiteLabelConfig.corPrimaria || '#EF443C',
                          color: 'white',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        {previewUrl && (
                          <Avatar src={previewUrl} sx={{ width: 40, height: 40 }} />
                        )}
                        <Typography variant="h6">
                          Sua Marca Personalizada
                        </Typography>
                      </Box>
                    </Box>

                    <PrimaryButton
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSaveWhiteLabel}
                      disabled={loading || (!!linkPersonalizado && linkDisponivel === false)}
                    >
                      Salvar Configuração White Label
                    </PrimaryButton>
                    
                    {linkPersonalizado && linkDisponivel === false && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        Escolha um link diferente antes de salvar
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />
          </>
        )}

        <Box component="form" onSubmit={handlePasswordUpdate}>
          <Typography variant="h6" gutterBottom>
            Alterar Senha
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="password"
                label="Senha Atual"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="password"
                label="Nova Senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="password"
                label="Confirmar Nova Senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                error={newPassword !== confirmPassword && confirmPassword !== ''}
                helperText={newPassword !== confirmPassword && confirmPassword !== '' ? "As senhas não coincidem" : ""}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PrimaryButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!currentPassword || !newPassword || !confirmPassword}
              >
                Atualizar Senha
              </PrimaryButton>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;
